import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Divider,
  LinearProgress,
  Chip
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import TimelineIcon from '@mui/icons-material/Timeline';
import RepeatIcon from '@mui/icons-material/Repeat';

/**
 * FrequencyMetrics Component
 * Displays detailed frequency metrics for a parameter in an expanded row
 *
 * @param {object} props
 * @param {object} props.data - Frequency data containing coverage, volume, and pattern info
 * @param {string} props.availableSince - Date when parameter became available
 * @param {string} props.sensitivity - Sensitivity level
 * @param {string} props.usagePattern - Usage pattern description
 */
const FrequencyMetrics = ({ data, availableSince, sensitivity, usagePattern }) => {
  const {
    caseCoverage = 0,
    totalCases = 0,
    casesWithParameter = 0,
    stationCoverage = [],
    avgMeasurementsPerCase = 0,
    medianMeasurementsPerCase = 0,
    totalMeasurements = 0,
    measurementPattern = {},
    continuousCases = 0
  } = data;

  /**
   * Group stations by category/area
   */
  const groupStationsByArea = (stations) => {
    const groups = {
      'Intensivstationen': { stations: [], totalPercentage: 0, color: '#1976d2' },
      'IMC (Intermediate Care)': { stations: [], totalPercentage: 0, color: '#42a5f5' },
      'Normalstationen': { stations: [], totalPercentage: 0, color: '#90caf9' },
      'OP & Diagnostik': { stations: [], totalPercentage: 0, color: '#4caf50' },
      'Labor': { stations: [], totalPercentage: 0, color: '#ff9800' },
      'Sonstige': { stations: [], totalPercentage: 0, color: '#9e9e9e' }
    };

    stations.forEach(station => {
      if (station.name.includes('ICU') || station.name.includes('NICU') || station.name.includes('PICU')) {
        groups['Intensivstationen'].stations.push(station);
        groups['Intensivstationen'].totalPercentage += station.percentage;
      } else if (station.name.includes('IMC')) {
        groups['IMC (Intermediate Care)'].stations.push(station);
        groups['IMC (Intermediate Care)'].totalPercentage += station.percentage;
      } else if (station.name.includes('Station') || station.name.includes('Stroke Unit')) {
        groups['Normalstationen'].stations.push(station);
        groups['Normalstationen'].totalPercentage += station.percentage;
      } else if (station.name.includes('OP') || station.name.includes('Anästhesie') || station.name.includes('Diagnostik')) {
        groups['OP & Diagnostik'].stations.push(station);
        groups['OP & Diagnostik'].totalPercentage += station.percentage;
      } else if (station.name.includes('Labor')) {
        groups['Labor'].stations.push(station);
        groups['Labor'].totalPercentage += station.percentage;
      } else {
        groups['Sonstige'].stations.push(station);
        groups['Sonstige'].totalPercentage += station.percentage;
      }
    });

    // Calculate average percentage per area
    Object.keys(groups).forEach(key => {
      const group = groups[key];
      if (group.stations.length > 0) {
        group.avgPercentage = Math.round(group.totalPercentage / group.stations.length);
      } else {
        group.avgPercentage = 0;
      }
    });

    // Return as sorted array (by avgPercentage descending)
    return Object.entries(groups)
      .map(([name, data]) => ({ name, ...data }))
      .filter(group => group.stations.length > 0)
      .sort((a, b) => b.avgPercentage - a.avgPercentage);
  };

  const stationGroups = groupStationsByArea(stationCoverage);

  return (
    <Box sx={{ p: 2, backgroundColor: '#fafafa' }}>
      <Grid container spacing={3}>
        {/* 1. Coverage / Verbreitung */}
        <Grid item xs={12} md={4}>
          <Box sx={{
            borderLeft: '4px solid #1976d2',
            pl: 2,
            backgroundColor: '#f5f9ff'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <LocationOnIcon sx={{ color: '#1976d2' }} />
              <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#1976d2' }}>
                Verbreitung
              </Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                {stationGroups.length} Stationsbereiche
              </Typography>
              {stationGroups.length > 0 ? (
                <>
                  {stationGroups.slice(0, 3).map((group, idx) => (
                    <Box key={idx} sx={{ mb: 1.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                        <Typography variant="caption" fontWeight={500}>
                          {group.name} ({group.stations.length})
                        </Typography>
                        <Typography variant="caption" fontWeight="bold">
                          {group.avgPercentage}%
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={group.avgPercentage}
                        sx={{
                          height: 6,
                          borderRadius: 1,
                          backgroundColor: '#e0e0e0',
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: group.color
                          }
                        }}
                      />
                    </Box>
                  ))}
                  {stationGroups.length > 3 && (
                    <Typography variant="caption" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                      ... weitere {stationGroups.length - 3} Bereiche
                    </Typography>
                  )}
                </>
              ) : (
                <Typography variant="caption" color="textSecondary">
                  Keine Daten verfügbar
                </Typography>
              )}
            </Box>

            <Divider sx={{ my: 1 }} />

            <Box>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Fallabdeckung
              </Typography>
              <Typography variant="h6" fontWeight="bold">
                {caseCoverage}% aller Fälle
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {casesWithParameter.toLocaleString()} von {totalCases.toLocaleString()} Fällen
              </Typography>
            </Box>
          </Box>
        </Grid>

        {/* 2. Volume / Messumfang */}
        <Grid item xs={12} md={4}>
          <Box sx={{
            borderLeft: '4px solid #2e7d32',
            pl: 2,
            backgroundColor: '#f5fef5'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <TimelineIcon sx={{ color: '#2e7d32' }} />
              <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#2e7d32' }}>
                Messumfang
              </Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Durchschnitt pro Fall
              </Typography>
              <Typography variant="h6" fontWeight="bold">
                ⌀ {avgMeasurementsPerCase} Messungen
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Median: {medianMeasurementsPerCase}
              </Typography>
            </Box>

            <Divider sx={{ my: 1 }} />

            <Box>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Gesamt
              </Typography>
              <Typography variant="h6" fontWeight="bold">
                {totalMeasurements.toLocaleString()}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Messungen (letztes Jahr)
              </Typography>
            </Box>
          </Box>
        </Grid>

        {/* 3. Pattern / Messmuster */}
        <Grid item xs={12} md={4}>
          <Box sx={{
            borderLeft: '4px solid #ed6c02',
            pl: 2,
            backgroundColor: '#fff8f5'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <RepeatIcon sx={{ color: '#ed6c02' }} />
              <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#ed6c02' }}>
                Messmuster
              </Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Typische Frequenz
              </Typography>
              <Chip
                label={measurementPattern.typical || 'Nicht verfügbar'}
                color="primary"
                variant="outlined"
                size="small"
                sx={{ mb: 0.5 }}
              />
              <Typography variant="caption" display="block" color="textSecondary">
                ⌀ {measurementPattern.avgInterval || 'N/A'} Intervall
              </Typography>
              <Typography variant="caption" display="block" color="textSecondary">
                σ {measurementPattern.stdDev || 'N/A'}
              </Typography>
            </Box>

            <Divider sx={{ my: 1 }} />

            <Box>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Kontinuierliche Messungen
              </Typography>
              <Typography variant="h6" fontWeight="bold">
                {((continuousCases / casesWithParameter) * 100).toFixed(0)}%
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {continuousCases.toLocaleString()} Fälle mit &gt;10 Messungen
              </Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>

    </Box>
  );
};

export default FrequencyMetrics;
